import { useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
  LoadingState,
  Modal,
  ModalFooter,
  Select,
  type Column,
} from "@agentnative/ui";
import { useAuthStore } from "../stores/auth";
import { useCurrentOrganizationId } from "../stores/currentOrg";
import { apiErrorMessage } from "../lib/api/client";
import {
  MEMBER_ROLE_OPTIONS,
  inviteMember,
  listMembers,
  memberRoleLabel,
  removeMember,
  updateMemberRole,
} from "../lib/api/org";
import type { MemberRole, OrganizationMember } from "../lib/api/types";

export function MembersRoute() {
  const organizationId = useCurrentOrganizationId();
  // Real authenticated caller (console-server's authMiddleware derives the
  // same value server-side from the session cookie and now REJECTS any
  // invited_by/updated_by/removed_by that doesn't match it -- see
  // console/services/org/service.go's requireAuthenticatedActor). This
  // must be the real logged-in user's id, not a dev-panel value; there is
  // no working fallback anymore, so member-management actions are simply
  // unavailable (buttons disabled below) until a real session exists.
  const actingUserId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<MemberRole>("MEMBER_ROLE_MEMBER");
  const [removeTarget, setRemoveTarget] = useState<OrganizationMember | null>(null);

  const membersQuery = useQuery({
    queryKey: ["members", organizationId],
    queryFn: () => listMembers(organizationId, { limit: 100, offset: 0 }),
    enabled: !!organizationId,
  });

  const inviteMutation = useMutation({
    mutationFn: () => {
      if (!actingUserId) throw new Error("Not logged in.");
      return inviteMember({
        organizationId,
        email: inviteEmail,
        role: inviteRole,
        invitedBy: actingUserId,
      });
    },
    onSuccess: () => {
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("MEMBER_ROLE_MEMBER");
      queryClient.invalidateQueries({ queryKey: ["members", organizationId] });
    },
  });

  const roleMutation = useMutation({
    mutationFn: (vars: { userId: string; role: MemberRole }) => {
      if (!actingUserId) throw new Error("Not logged in.");
      return updateMemberRole({
        organizationId,
        userId: vars.userId,
        role: vars.role,
        updatedBy: actingUserId,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["members", organizationId] }),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => {
      if (!actingUserId) throw new Error("Not logged in.");
      return removeMember(organizationId, userId, actingUserId);
    },
    onSuccess: () => {
      setRemoveTarget(null);
      queryClient.invalidateQueries({ queryKey: ["members", organizationId] });
    },
  });

  if (!organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Set an organization_id in the dev auth panel above to manage members."
      />
    );
  }

  const columns: Column<OrganizationMember>[] = [
    { key: "userId", label: "User ID", render: (m) => <span className="font-mono text-sm">{m.userId}</span> },
    {
      key: "role",
      label: "Role",
      render: (m) => (
        <Select
          size="sm"
          value={m.role}
          disabled={roleMutation.isPending || !actingUserId}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            roleMutation.mutate({ userId: m.userId, role: e.target.value as MemberRole })
          }
          options={MEMBER_ROLE_OPTIONS}
        />
      ),
    },
    {
      key: "joinedAt",
      label: "Joined",
      render: (m) => (m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : "-"),
    },
    {
      key: "actions",
      label: "",
      render: (m) => (
        <Button variant="danger" size="sm" disabled={!actingUserId} onClick={() => setRemoveTarget(m)}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Members</h1>
        <Button disabled={!actingUserId} onClick={() => setInviteOpen(true)}>
          Invite Member
        </Button>
      </div>

      {!actingUserId && (
        <Alert variant="warning" title="Not logged in">
          Member management requires a real login session (`agentnative auth login` equivalent
          for the web console) -- invite/role-change/remove are disabled until one exists.
        </Alert>
      )}

      {inviteMutation.isError && (
        <Alert variant="danger" title="Failed to invite member">
          {apiErrorMessage(inviteMutation.error)}
        </Alert>
      )}
      {roleMutation.isError && (
        <Alert variant="danger" title="Failed to update role">
          {apiErrorMessage(roleMutation.error)}
        </Alert>
      )}
      {removeMutation.isError && (
        <Alert variant="danger" title="Failed to remove member">
          {apiErrorMessage(removeMutation.error)}
        </Alert>
      )}

      {membersQuery.isLoading ? (
        <LoadingState message="Loading members..." />
      ) : membersQuery.isError ? (
        <Alert variant="danger" title="Failed to load members">
          {apiErrorMessage(membersQuery.error)}
        </Alert>
      ) : (
        <Card padding="none">
          <DataTable<OrganizationMember>
            data={membersQuery.data?.members ?? []}
            columns={columns}
            emptyMessage="No members yet."
          />
        </Card>
      )}

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite Member">
        <div className="space-y-4">
          <Input
            label="Email"
            type="email"
            required
            value={inviteEmail}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setInviteEmail(e.target.value)}
            placeholder="teammate@example.com"
          />
          <Select
            label="Role"
            value={inviteRole}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setInviteRole(e.target.value as MemberRole)}
            options={MEMBER_ROLE_OPTIONS}
          />
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setInviteOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={inviteMutation.isPending}
            disabled={!inviteEmail || !actingUserId}
            onClick={() => inviteMutation.mutate()}
          >
            Send Invite
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title="Remove Member"
        size="sm"
      >
        {removeTarget && (
          <p>
            Remove <span className="font-mono">{removeTarget.userId}</span> (
            {memberRoleLabel(removeTarget.role)}) from this organization?
          </p>
        )}
        <ModalFooter>
          <Button variant="secondary" onClick={() => setRemoveTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={removeMutation.isPending}
            disabled={!actingUserId}
            onClick={() => removeTarget && removeMutation.mutate(removeTarget.userId)}
          >
            Remove
          </Button>
        </ModalFooter>
      </Modal>

      <p className="text-xs text-[var(--color-neutral-500)]">
        <Badge variant="info" size="sm">
          note
        </Badge>{" "}
        Invited members show up here once they accept via the invitation link (AcceptInvitation
        RPC); pending invitations themselves are not listed by any endpoint yet.
      </p>
    </div>
  );
}
